import type { TFunction } from '@mukil/i18n';
import {
  DatePicker,
  FormField,
  Input,
  StyledSelect,
  Switch,
} from '@jumpstone/ui-react';
import {
  type EmployeeFormDraft,
  type EmployeeFormErrors,
  type EmployeeFormFieldError,
  type EmployeeJurisdiction,
} from './employee_form_validation';
import { EmployeeFormJurisdictionFields } from './employee_form_jurisdiction_fields';
import { EmployeePayAmountField } from './employee_pay_amount_field';
import { EmployeeCompensationSummary } from './employee_compensation_summary';
import { EmployeeFormCompensationFields } from './employee_form_compensation_fields';
import type { PayCurrencyOption } from '../../data/use_pay_currencies';
import { EmployeeFormWorkerTypeFields } from './employee_form_worker_type_fields';
import { EmployeeNumberField } from './employee_form_identity_fields';
import type { EmploymentType } from '../../data/types';
import { useDatePickerLabels } from '../../lib/use_date_picker_labels';

export const SELECT_TRIGGER_CLASSES =
  'nv-input flex h-9 w-full cursor-pointer items-center justify-between gap-2';

const FIELD_ERROR_KEY: Record<EmployeeFormFieldError, string> = {
  required: 'employees.form_error_required',
  too_long: 'employees.form_error_too_long',
  invalid_email: 'employees.form_error_invalid_email',
  salary_required: 'employees.form_error_salary_required',
  negative_amount: 'employees.form_error_negative_amount',
  termination_before_start: 'employees.form_error_termination_before_start',
  invalid_nic: 'employees.form_error_invalid_nic',
  invalid_nic_day: 'employees.form_error_invalid_nic_day',
  epf_empty: 'employees.form_error_epf_empty',
  epf_control_chars: 'employees.form_error_epf_control_chars',
  invalid_risk_class: 'employees.form_error_invalid_risk_class',
  fte_required: 'employees.form_error_fte_required',
  fte_out_of_range: 'employees.form_error_fte_out_of_range',
  effective_from_before_start:
    'employees.form_error_effective_from_before_start',
  history_invalid: 'employees.form_error_history_invalid',
};

export interface EmployeeFormFieldsProps {
  draft: EmployeeFormDraft;
  errors: EmployeeFormErrors;
  mode: 'create' | 'edit';
  jurisdiction: EmployeeJurisdiction;
  /**
   * The organisation's base currency code (e.g. `LKR`) -- what an employee
   * with no explicit pay currency (`draft.pay_currency_code === ''`) is paid in.
   */
  currency: string;
  /** Options for the per-employee pay-currency picker (`usePayCurrencies`). */
  payCurrencies: readonly PayCurrencyOption[];
  locale: string;
  t: TFunction;
  /** EDIT mode only: date the employee's current pay took effect, if known. */
  salarySince?: string | null | undefined;
  set: <K extends keyof EmployeeFormDraft>(
    key: K,
    value: EmployeeFormDraft[K],
  ) => void;
  /** Worker-type change -- the dialog also clears the FTE + dependent errors. */
  on_type_change: (type: EmploymentType) => void;
  /** The SAVED worker type (edit form); `null`/omitted on create. */
  original_employment_type?: EmploymentType | null;
}

/**
 * The employee form's core field grid (identity, employment/pay, bank
 * details) -- pure presentation, no state of its own (every value/handler is
 * passed in). The two jurisdiction-specific ID groups live in
 * `employee_form_jurisdiction_fields.tsx` (split out purely to keep both
 * files under CLAUDE.md's 150-line component guidance). Split out of
 * `employee_form_dialog.tsx` so the dialog shell stays focused on
 * state/submit wiring and this file stays focused on "what does each field
 * look like."
 */
export function EmployeeFormFields({
  draft,
  errors,
  mode,
  jurisdiction,
  currency,
  payCurrencies,
  locale,
  t,
  salarySince,
  set,
  on_type_change,
  original_employment_type = null,
}: EmployeeFormFieldsProps): React.JSX.Element {
  const datePickerLabels = useDatePickerLabels();
  // A contractor is never paid through a pay run: no pay basis or
  // salary/rate to record (they are paid through bills).
  const is_contractor = draft.employment_type === 'contractor';
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
      <FormField
        label={t('employees.form_full_name')}
        htmlFor="employee-form-full-name"
        required
        helpText={t('employees.form_full_name_hint')}
        error={errors.full_name && t(FIELD_ERROR_KEY[errors.full_name])}
      >
        <Input
          id="employee-form-full-name"
          value={draft.full_name}
          onChange={(e) => {
            set('full_name', e.target.value);
          }}
        />
      </FormField>

      <FormField
        label={t('employees.form_email')}
        htmlFor="employee-form-email"
        error={errors.email && t(FIELD_ERROR_KEY[errors.email])}
      >
        <Input
          id="employee-form-email"
          type="email"
          value={draft.email}
          onChange={(e) => {
            set('email', e.target.value);
          }}
        />
      </FormField>

      <EmployeeNumberField draft={draft} errors={errors} t={t} set={set} />

      <EmployeeFormWorkerTypeFields
        draft={draft}
        original_employment_type={original_employment_type}
        fte_error={errors.fte_percent}
        t={t}
        on_type_change={on_type_change}
        on_fte_change={(value) => {
          set('fte_percent', value);
        }}
      />

      {is_contractor && (
        <p
          className="nv-help sm:col-span-2"
          data-testid="employee-form-contractor-pay-note"
        >
          {t('employees.form_contractor_pay_note')}
        </p>
      )}

      {is_contractor ? null : mode === 'edit' ? (
        // Pay is effective-dated, so it is NOT editable in place on an
        // existing employee: an overwrite would silently rewrite history.
        // Changes go through the Change salary dialog instead.
        <EmployeeCompensationSummary
          pay_basis={draft.pay_basis}
          amount_cents={
            draft.pay_basis === 'salaried'
              ? draft.monthly_salary_cents
              : draft.hourly_rate_cents
          }
          currency={draft.pay_currency_code || currency}
          effective_since={salarySince ?? null}
          locale={locale}
          t={t}
        />
      ) : (
        <>
          <FormField label={t('employees.form_pay_basis')}>
            <StyledSelect
              value={draft.pay_basis}
              onChange={(value) => {
                set('pay_basis', value);
              }}
              options={[
                {
                  value: 'hourly',
                  label: t('employees.form_pay_basis_hourly'),
                },
                {
                  value: 'salaried',
                  label: t('employees.form_pay_basis_salaried'),
                },
              ]}
              ariaLabel={t('employees.form_pay_basis')}
              triggerClassName={SELECT_TRIGGER_CLASSES}
            />
          </FormField>

          {draft.pay_basis === 'salaried' ? (
            <EmployeePayAmountField
              id="employee-form-monthly-salary"
              label={t('employees.form_monthly_salary', {
                currency: draft.pay_currency_code || currency,
              })}
              error={
                errors.monthly_salary_cents &&
                t(FIELD_ERROR_KEY[errors.monthly_salary_cents])
              }
              amountCents={draft.monthly_salary_cents}
              onAmountChange={(cents) => {
                set('monthly_salary_cents', cents);
              }}
              payCurrencyCode={draft.pay_currency_code}
              onPayCurrencyChange={(code) => {
                set('pay_currency_code', code);
              }}
              baseCurrency={currency}
              payCurrencies={payCurrencies}
              t={t}
            />
          ) : (
            <EmployeePayAmountField
              id="employee-form-hourly-rate"
              label={t('employees.form_hourly_rate_currency', {
                currency: draft.pay_currency_code || currency,
              })}
              error={
                errors.hourly_rate_cents &&
                t(FIELD_ERROR_KEY[errors.hourly_rate_cents])
              }
              amountCents={draft.hourly_rate_cents}
              onAmountChange={(cents) => {
                set('hourly_rate_cents', cents);
              }}
              payCurrencyCode={draft.pay_currency_code}
              onPayCurrencyChange={(code) => {
                set('pay_currency_code', code);
              }}
              baseCurrency={currency}
              payCurrencies={payCurrencies}
              t={t}
            />
          )}
        </>
      )}

      <FormField
        label={t('employees.form_start_date')}
        htmlFor="employee-form-start-date"
      >
        <DatePicker
          id="employee-form-start-date"
          value={draft.employment_start_date || null}
          locale={locale}
          labels={datePickerLabels}
          aria-label={t('employees.form_start_date')}
          onChange={(iso) => {
            set('employment_start_date', iso);
          }}
        />
      </FormField>

      {mode === 'create' && !is_contractor && (
        <EmployeeFormCompensationFields
          draft={draft}
          errors={errors}
          locale={locale}
          t={t}
          set={set}
        />
      )}

      {mode === 'edit' && (
        <FormField
          label={t('employees.form_termination_date')}
          htmlFor="employee-form-termination-date"
          error={
            errors.termination_date &&
            t(FIELD_ERROR_KEY[errors.termination_date])
          }
        >
          <DatePicker
            id="employee-form-termination-date"
            value={draft.termination_date || null}
            locale={locale}
            labels={datePickerLabels}
            aria-label={t('employees.form_termination_date')}
            error={Boolean(errors.termination_date)}
            onChange={(iso) => {
              set('termination_date', iso);
            }}
          />
        </FormField>
      )}

      {mode === 'edit' && (
        <div className="flex items-center justify-between rounded-lg border border-[color:var(--line)] px-3 py-2">
          <span className="nv-label mb-0">{t('employees.form_is_active')}</span>
          <Switch
            checked={draft.is_active}
            onCheckedChange={(checked) => {
              set('is_active', checked);
            }}
            aria-label={t('employees.form_is_active')}
          />
        </div>
      )}

      <FormField
        label={t('employees.form_job_title')}
        htmlFor="employee-form-job-title"
      >
        <Input
          id="employee-form-job-title"
          value={draft.job_title}
          onChange={(e) => {
            set('job_title', e.target.value);
          }}
        />
      </FormField>

      <FormField
        label={t('employees.form_department')}
        htmlFor="employee-form-department"
      >
        <Input
          id="employee-form-department"
          value={draft.department}
          onChange={(e) => {
            set('department', e.target.value);
          }}
        />
      </FormField>

      <FormField
        label={t('employees.form_work_location')}
        htmlFor="employee-form-work-location"
        helpText={t('employees.form_work_location_hint')}
      >
        <Input
          id="employee-form-work-location"
          value={draft.work_location}
          onChange={(e) => {
            set('work_location', e.target.value);
          }}
        />
      </FormField>

      <FormField
        label={t('employees.form_bank_name')}
        htmlFor="employee-form-bank-name"
      >
        <Input
          id="employee-form-bank-name"
          value={draft.bank_name}
          onChange={(e) => {
            set('bank_name', e.target.value);
          }}
        />
      </FormField>

      <FormField
        label={t('employees.form_bank_account_number')}
        htmlFor="employee-form-bank-account-number"
      >
        <Input
          id="employee-form-bank-account-number"
          value={draft.bank_account_number}
          inputMode="numeric"
          onChange={(e) => {
            set('bank_account_number', e.target.value);
          }}
        />
      </FormField>

      <FormField
        label={t('employees.form_bank_branch')}
        htmlFor="employee-form-bank-branch"
      >
        {/*
          No `inputMode` -- Branch is the branch NAME ("Colombo Fort"), stored
          as free `text` with no numeric validation, and the numeric branch
          identifier has its own "Branch code" field below. It carried
          `inputMode="numeric"` from this epic's mobile-keyboard sweep, which
          swept it in alongside Account number and left phones offering a
          digits-only keypad for a field users type words into.
        */}
        <Input
          id="employee-form-bank-branch"
          value={draft.bank_branch}
          onChange={(e) => {
            set('bank_branch', e.target.value);
          }}
        />
      </FormField>

      <FormField
        label={t('employees.form_bank_code')}
        htmlFor="employee-form-bank-code"
      >
        <Input
          id="employee-form-bank-code"
          value={draft.bank_code}
          onChange={(e) => {
            set('bank_code', e.target.value);
          }}
        />
      </FormField>

      <EmployeeFormJurisdictionFields
        draft={draft}
        errors={errors}
        jurisdiction={jurisdiction}
        t={t}
        set={set}
      />
    </div>
  );
}
